/**
 * Every path the client calls, in one place.
 *
 * <p>Not a style preference: the API versions its routes, and a rename that misses one string
 * literal buried in a screen fails at runtime with a 404 that looks like a permissions problem.
 * Collected here, a route change is one edit and the compiler finds the callers.</p>
 */
export const endpoints = {
  auth: {
    login: "/api/v1/auth/login",
    refresh: "/api/v1/auth/refresh",
    logout: "/api/v1/auth/logout",
  },

  me: "/api/v1/me",

  platform: {
    modules: "/api/v1/platform/modules",
    featureFlags: "/api/v1/platform/feature-flags",
    featureFlag: (key: string) => `/api/v1/platform/feature-flags/${key}`,
  },

  integration: {
    status: "/api/v1/integrations/healntrack/status",
    sync: "/api/v1/integrations/healntrack/sync",
    /** Takes `uhid` as a query parameter, so any character a UHID holds survives the trip. */
    patient: "/api/v1/integrations/healntrack/patients",
  },

  master: {
    departments: "/api/v1/master/departments",
    department: (id: string) => `/api/v1/master/departments/${id}`,
    units: "/api/v1/master/units",
    unit: (id: string) => `/api/v1/master/units/${id}`,
    designations: "/api/v1/master/designations",
    designation: (id: string) => `/api/v1/master/designations/${id}`,
    locations: "/api/v1/master/locations",
    location: (id: string) => `/api/v1/master/locations/${id}`,
    employees: "/api/v1/master/employees",
    employee: (id: string) => `/api/v1/master/employees/${id}`,
    employeeIdentity: (id: string) => `/api/v1/master/employees/${id}/identity`,
    employeeWorkforce: (id: string) => `/api/v1/master/employees/${id}/workforce`,
  },

  workforce: {
    shifts: "/api/v1/workforce/shifts",
    shift: (id: string) => `/api/v1/workforce/shifts/${id}`,
    staffCategories: "/api/v1/workforce/staff-categories",
    staffCategory: (id: string) => `/api/v1/workforce/staff-categories/${id}`,
    skills: "/api/v1/workforce/skills",
    skill: (id: string) => `/api/v1/workforce/skills/${id}`,
    employmentTypes: "/api/v1/workforce/employment-types",
    employmentType: (id: string) => `/api/v1/workforce/employment-types/${id}`,
    dutyTypes: "/api/v1/workforce/duty-types",
    dutyType: (id: string) => `/api/v1/workforce/duty-types/${id}`,
    leaveTypes: "/api/v1/workforce/leave-types",
    leaveType: (id: string) => `/api/v1/workforce/leave-types/${id}`,
    holidayCalendars: "/api/v1/workforce/holiday-calendars",
    holidayCalendar: (id: string) => `/api/v1/workforce/holiday-calendars/${id}`,
    holidays: (calendarId: string) => `/api/v1/workforce/holiday-calendars/${calendarId}/holidays`,
    holiday: (id: string) => `/api/v1/workforce/holiday-calendars/holidays/${id}`,
    weeklyOffPatterns: "/api/v1/workforce/weekly-off-patterns",
    weeklyOffPattern: (id: string) => `/api/v1/workforce/weekly-off-patterns/${id}`,
  },

  roster: {
    list: "/api/v1/workforce/rosters",
    item: (id: string) => `/api/v1/workforce/rosters/${id}`,
    grid: (id: string) => `/api/v1/workforce/rosters/${id}/grid`,
    conflicts: (id: string) => `/api/v1/workforce/rosters/${id}/conflicts`,
    check: (id: string) => `/api/v1/workforce/rosters/${id}/check`,
    transition: (id: string) => `/api/v1/workforce/rosters/${id}/transition`,
    suggestions: (id: string) => `/api/v1/workforce/rosters/${id}/suggestions`,
    assignments: (id: string) => `/api/v1/workforce/rosters/${id}/assignments`,
    assignment: (assignmentId: string) => `/api/v1/workforce/rosters/assignments/${assignmentId}`,
    removeAssignments: "/api/v1/workforce/rosters/assignments/remove",
    overrideConflict: (conflictId: string) => `/api/v1/workforce/rosters/conflicts/${conflictId}/override`,
    approvals: (id: string) => `/api/v1/workforce/rosters/${id}/approvals`,
    day: (date: string) => `/api/v1/workforce/duty/day/${date}`,
  },

  swaps: {
    list: "/api/v1/workforce/swaps",
    item: (id: string) => `/api/v1/workforce/swaps/${id}`,
    duties: "/api/v1/workforce/swaps/duties",
    colleagues: "/api/v1/workforce/swaps/colleagues",
    respond: (id: string) => `/api/v1/workforce/swaps/${id}/respond`,
    decide: (id: string) => `/api/v1/workforce/swaps/${id}/decide`,
    cancel: (id: string) => `/api/v1/workforce/swaps/${id}/cancel`,
  },

  substitutions: {
    list: "/api/v1/workforce/substitutions",
    candidates: "/api/v1/workforce/substitutions/candidates",
  },

  leave: {
    requests: "/api/v1/workforce/leave/requests",
    decide: (id: string) => `/api/v1/workforce/leave/requests/${id}/decide`,
    cancel: (id: string) => `/api/v1/workforce/leave/requests/${id}/cancel`,
    balances: "/api/v1/workforce/leave/balances",
    /** Active leave types — open to anyone who may request leave, unlike the leave-type master. */
    types: "/api/v1/workforce/leave/types",
    entitlements: "/api/v1/workforce/leave/entitlements",
    entitlement: (id: string) => `/api/v1/workforce/leave/entitlements/${id}`,
    allocate: "/api/v1/workforce/leave/entitlements/allocate",
  },

  reports: {
    catalog: "/api/v1/reports",
    run: (key: string) => `/api/v1/reports/${key}/run`,
    export: (key: string, format: string) => `/api/v1/reports/${key}/export?format=${format}`,
  },

  dashboard: "/api/v1/workforce/dashboard",

  notifications: {
    list: "/api/v1/notifications",
    unreadCount: "/api/v1/notifications/unread-count",
    read: (id: string) => `/api/v1/notifications/${id}/read`,
    readAll: "/api/v1/notifications/read-all",
    preferences: "/api/v1/notifications/preferences",
    push: "/api/v1/notifications/push",
    pushSubscriptions: "/api/v1/notifications/push/subscriptions",
    pushUnsubscribe: "/api/v1/notifications/push/unsubscribe",
  },

  attendance: {
    day: (date: string) => `/api/v1/workforce/attendance/day/${date}`,
    record: (id: string) => `/api/v1/workforce/attendance/records/${id}`,
    reconcile: "/api/v1/workforce/attendance/reconcile",
    manualPunch: "/api/v1/workforce/attendance/punches/manual",
    override: "/api/v1/workforce/attendance/override",
    exceptions: "/api/v1/workforce/attendance/exceptions",
    resolveException: (id: string) => `/api/v1/workforce/attendance/exceptions/${id}/resolve`,
    corrections: "/api/v1/workforce/attendance/corrections",
    decideCorrection: (id: string) => `/api/v1/workforce/attendance/corrections/${id}/decide`,
    cancelCorrection: (id: string) => `/api/v1/workforce/attendance/corrections/${id}/cancel`,
    overtime: "/api/v1/workforce/attendance/overtime",
    decideOvertime: (recordId: string) => `/api/v1/workforce/attendance/records/${recordId}/overtime`,
    policy: "/api/v1/workforce/attendance/policy",
    devices: "/api/v1/workforce/attendance/devices",
    device: (id: string) => `/api/v1/workforce/attendance/devices/${id}`,
    adapters: "/api/v1/workforce/attendance/devices/adapters",
    rotateKey: (id: string) => `/api/v1/workforce/attendance/devices/${id}/rotate-key`,
    pullNow: (id: string) => `/api/v1/workforce/attendance/devices/${id}/pull`,
    enrolments: "/api/v1/workforce/attendance/enrolments",
    enrolment: (id: string) => `/api/v1/workforce/attendance/enrolments/${id}`,
    marking: (date: string) => `/api/v1/workforce/attendance/marking/${date}`,
    mark: "/api/v1/workforce/attendance/marking",
    modes: "/api/v1/workforce/attendance/modes",
  },

  theatre: {
    day: (date: string) => `/api/v1/workforce/theatre/day/${date}`,
    schedule: "/api/v1/workforce/theatre/schedule",
    cases: "/api/v1/workforce/theatre/cases",
    caseItem: (id: string) => `/api/v1/workforce/theatre/cases/${id}`,
    reschedule: (id: string) => `/api/v1/workforce/theatre/cases/${id}/schedule`,
    status: (id: string) => `/api/v1/workforce/theatre/cases/${id}/status`,
    team: (id: string) => `/api/v1/workforce/theatre/cases/${id}/team`,
    candidates: (id: string) => `/api/v1/workforce/theatre/cases/${id}/candidates`,
    removeMember: (assignmentId: string) => `/api/v1/workforce/theatre/team/${assignmentId}/remove`,
    replaceMember: (assignmentId: string) => `/api/v1/workforce/theatre/team/${assignmentId}/replace`,
    theatres: "/api/v1/workforce/theatre/theatres",
    theatreItem: (id: string) => `/api/v1/workforce/theatre/theatres/${id}`,
    closures: "/api/v1/workforce/theatre/closures",
    closure: (id: string) => `/api/v1/workforce/theatre/closures/${id}`,
    roles: "/api/v1/workforce/theatre/roles",
    role: (id: string) => `/api/v1/workforce/theatre/roles/${id}`,
    procedures: "/api/v1/workforce/theatre/procedures",
    procedure: (id: string) => `/api/v1/workforce/theatre/procedures/${id}`,
  },

  my: {
    today: "/api/v1/workforce/my/today",
    calendar: "/api/v1/workforce/my/calendar",
    attendance: "/api/v1/workforce/my/attendance",
    correction: "/api/v1/workforce/my/attendance/corrections",
    confirmTheatre: (assignmentId: string) => `/api/v1/workforce/my/theatre/${assignmentId}/confirm`,
  },

  shiftChanges: {
    list: "/api/v1/workforce/shift-changes",
    duties: "/api/v1/workforce/shift-changes/duties",
    options: "/api/v1/workforce/shift-changes/options",
    decide: (id: string) => `/api/v1/workforce/shift-changes/${id}/decide`,
    cancel: (id: string) => `/api/v1/workforce/shift-changes/${id}/cancel`,
  },

  users: {
    list: "/api/v1/platform/users",
    item: (id: string) => `/api/v1/platform/users/${id}`,
    staffLogins: "/api/v1/platform/users/staff-logins",
    resetPassword: (id: string) => `/api/v1/platform/users/${id}/reset-password`,
    roles: "/api/v1/platform/roles",
    role: (id: string) => `/api/v1/platform/roles/${id}`,
    permissions: "/api/v1/platform/roles/permissions",
    changePassword: "/api/v1/auth/change-password",
  },

  rules: {
    catalog: "/api/v1/workforce/roster-rules/catalog",
    list: "/api/v1/workforce/roster-rules",
    item: (id: string) => `/api/v1/workforce/roster-rules/${id}`,
    staffing: "/api/v1/workforce/staffing-requirements",
    staffingItem: (id: string) => `/api/v1/workforce/staffing-requirements/${id}`,
  },
} as const;

/** The envelope every list endpoint returns. */
export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}
